import { useState, useMemo } from 'react'
import { useChain } from '../hooks/useChain'
import type { ChainItem } from '../api'

interface OptionChainProps {
  underlying?: string
  maxDte?: number
}

export function OptionChain({ underlying = 'BTCUSDT', maxDte = 60 }: OptionChainProps) {
  const { data, loading, error, run } = useChain({ underlying, max_dte: String(maxDte) })
  const [filterType, setFilterType] = useState<'ALL' | 'CALL' | 'PUT'>('ALL')
  const [filterExpiry, setFilterExpiry] = useState('ALL')
  const [strikeFrom, setStrikeFrom] = useState('')
  const [strikeTo, setStrikeTo] = useState('')

  const expiries = useMemo(() => {
    if (!data?.chain) return []
    const set = new Set(data.chain.map((c) => c.expiryDate))
    return Array.from(set).sort()
  }, [data])

  const filtered = useMemo(() => {
    if (!data?.chain) return []
    let items: ChainItem[] = [...data.chain]

    if (filterType !== 'ALL') {
      items = items.filter((c) => c.type === filterType)
    }
    if (filterExpiry !== 'ALL') {
      items = items.filter((c) => c.expiryDate === filterExpiry)
    }
    if (strikeFrom) {
      items = items.filter((c) => Number(c.strike) >= Number(strikeFrom))
    }
    if (strikeTo) {
      items = items.filter((c) => Number(c.strike) <= Number(strikeTo))
    }

    return items
  }, [data, filterType, filterExpiry, strikeFrom, strikeTo])

  const spot = data?.spot_price ?? 0

  function isITM(c: ChainItem) {
    if (c.strike == null || !spot) return false
    const strike = Number(c.strike)
    if (c.type === 'CALL') return strike < spot
    return strike > spot
  }

  function formatExpiry(d: string) {
    try {
      const dt = new Date(d)
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    } catch {
      return d
    }
  }

  const hasActiveFilters = filterType !== 'ALL' || filterExpiry !== 'ALL' || !!strikeFrom || !!strikeTo

  return (
    <div className="card">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-bold text-white">Option Chain</h2>
          <p className="text-[11px] text-gray-500">
            {underlying} · Spot{' '}
            <span className="font-mono font-semibold text-ev-accent">
              ${spot.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>{' '}
            · {filtered.length} contracts
          </p>
        </div>
        <button
          onClick={() => run({ underlying, max_dte: String(maxDte) })}
          disabled={loading}
          className="rounded-md bg-ev-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Loading...' : '↻ Refresh'}
        </button>
      </div>

      {/* Filters - responsive */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as 'ALL' | 'CALL' | 'PUT')}
          className="rounded-md border border-ev-border bg-ev-surface px-2 py-1.5 text-gray-300 focus:border-ev-accent focus:outline-none"
        >
          <option value="ALL">All Types</option>
          <option value="CALL">CALL</option>
          <option value="PUT">PUT</option>
        </select>

        <select
          value={filterExpiry}
          onChange={(e) => setFilterExpiry(e.target.value)}
          className="rounded-md border border-ev-border bg-ev-surface px-2 py-1.5 text-gray-300 focus:border-ev-accent focus:outline-none"
        >
          <option value="ALL">All Expiries</option>
          {expiries.map((e) => (
            <option key={e} value={e}>{formatExpiry(e)}</option>
          ))}
        </select>

        <div className="flex items-center gap-1">
          <span className="text-gray-500">Strike:</span>
          <input
            type="number"
            value={strikeFrom}
            onChange={(e) => setStrikeFrom(e.target.value)}
            placeholder="From"
            className="w-20 rounded-md border border-ev-border bg-ev-surface px-2 py-1.5 text-gray-300 placeholder-gray-600 focus:border-ev-accent focus:outline-none"
          />
          <span className="text-gray-600">–</span>
          <input
            type="number"
            value={strikeTo}
            onChange={(e) => setStrikeTo(e.target.value)}
            placeholder="To"
            className="w-20 rounded-md border border-ev-border bg-ev-surface px-2 py-1.5 text-gray-300 placeholder-gray-600 focus:border-ev-accent focus:outline-none"
          />
        </div>

        {hasActiveFilters && (
          <button
            onClick={() => { setFilterType('ALL'); setFilterExpiry('ALL'); setStrikeFrom(''); setStrikeTo('') }}
            className="rounded-md border border-ev-border px-2 py-1.5 text-gray-500 hover:text-gray-300 transition-colors"
          >
            ✕ Clear
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
          {error}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <p className="py-8 text-center text-sm text-gray-500">No contracts match filters.</p>
      )}

      {filtered.length > 0 && (
        <>
          {/* Mobile card layout */}
          <div className="space-y-2 md:hidden">
            {filtered.slice(0, 100).map((c, i) => {
              const itm = isITM(c)
              return (
                <div
                  key={i}
                  className={`rounded-xl border px-3 py-2.5 text-xs ${
                    itm
                      ? 'border-amber-700/30 bg-amber-900/10'
                      : 'border-ev-border bg-ev-surface/50'
                  }`}
                >
                  <div className="mb-1.5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-white">
                        {c.strike != null ? `$${Number(c.strike).toLocaleString()}` : '—'}
                      </span>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                        c.type === 'CALL'
                          ? 'bg-emerald-900/60 text-emerald-400 border border-emerald-700/40'
                          : 'bg-rose-900/60 text-rose-400 border border-rose-700/40'
                      }`}>
                        {c.type}
                      </span>
                      {itm && <span className="text-[9px] text-amber-500">● ITM</span>}
                    </div>
                    <span className="font-mono font-medium text-white">
                      {c.markPrice != null ? `$${Number(c.markPrice).toFixed(2)}` : (c.bid != null && c.ask != null ? `$${((Number(c.bid) + Number(c.ask)) / 2).toFixed(2)}` : '—')}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-x-2 gap-y-1 text-[11px]">
                    <div>
                      <span className="text-gray-500">Bid</span>
                      <span className="ml-1 font-mono text-gray-300">{c.bid != null ? Number(c.bid).toFixed(2) : '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Ask</span>
                      <span className="ml-1 font-mono text-gray-300">{c.ask != null ? Number(c.ask).toFixed(2) : '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Vol</span>
                      <span className="ml-1 font-mono text-gray-300">{c.volume ?? '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">OI</span>
                      <span className="ml-1 font-mono text-gray-300">{c.openInterest ?? '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">DTE</span>
                      <span className="ml-1 font-mono text-gray-300">{c.dte != null ? c.dte : '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">IV</span>
                      <span className="ml-1 font-mono text-gray-300">{c.iv ? `${(Number(c.iv) * 100).toFixed(1)}%` : '—'}</span>
                    </div>
                    <div className="col-span-2 text-right">
                      <span className="text-gray-500">Exp</span>
                      <span className="ml-1 font-mono text-gray-300">{formatExpiry(c.expiryDate)}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Desktop table layout */}
          <div className="table-scroll hidden md:block">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-ev-border text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Strike</th>
                  <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Type</th>
                  <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Expiry</th>
                  <th className="whitespace-nowrap px-2 py-2.5 font-semibold">DTE</th>
                  <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Bid</th>
                  <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Ask</th>
                  <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Mark</th>
                  <th className="whitespace-nowrap px-2 py-2.5 font-semibold">Volume</th>
                  <th className="whitespace-nowrap px-2 py-2.5 font-semibold">OI</th>
                  <th className="whitespace-nowrap px-2 py-2.5 font-semibold">IV</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 100).map((c, i) => {
                  const itm = isITM(c)
                  const rowBg = itm ? 'bg-amber-500/5' : ''
                  return (
                    <tr
                      key={i}
                      className={`border-b border-ev-border/50 last:border-0 row-hover ${rowBg}`}
                    >
                      <td className="whitespace-nowrap px-2 py-2.5 font-mono font-semibold">
                        {c.strike != null ? Number(c.strike).toLocaleString() : '—'}
                        {itm && <span className="ml-1.5 text-[9px] text-amber-500">●</span>}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2.5">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                          c.type === 'CALL'
                            ? 'bg-emerald-900/60 text-emerald-400 border border-emerald-700/40'
                            : 'bg-rose-900/60 text-rose-400 border border-rose-700/40'
                        }`}>
                          {c.type}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2.5 text-gray-400">{formatExpiry(c.expiryDate)}</td>
                      <td className="whitespace-nowrap px-2 py-2.5 font-mono text-gray-400">{c.dte != null ? c.dte : '—'}</td>
                      <td className="whitespace-nowrap px-2 py-2.5 font-mono">{c.bid != null ? Number(c.bid).toFixed(2) : '—'}</td>
                      <td className="whitespace-nowrap px-2 py-2.5 font-mono">{c.ask != null ? Number(c.ask).toFixed(2) : '—'}</td>
                      <td className="whitespace-nowrap px-2 py-2.5 font-mono font-medium text-white">
                        {c.markPrice != null ? Number(c.markPrice).toFixed(2) : (c.bid != null && c.ask != null ? ((Number(c.bid) + Number(c.ask)) / 2).toFixed(2) : '—')}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2.5 font-mono text-gray-400">{c.volume != null ? c.volume : '—'}</td>
                      <td className="whitespace-nowrap px-2 py-2.5 font-mono text-gray-400">{c.openInterest != null ? c.openInterest : '—'}</td>
                      <td className="whitespace-nowrap px-2 py-2.5 font-mono text-gray-400">
                        {c.iv ? `${(Number(c.iv) * 100).toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {filtered.length > 100 && (
            <p className="mt-2 text-[11px] text-gray-500">+ {filtered.length - 100} more contracts</p>
          )}
        </>
      )}
    </div>
  )
}
